import _ from "lodash";

import { isFailure, ListType, UrlType } from "@sportal/api";

import { NotificationTypes, Notificator } from "../../components/notification";
import { URL_TYPES } from "../../common/utils/url_types";
import {
  GLOBAL_URL_FILTER_ID,
  EMPTY_CSV_ERROR,
  NOT_SUPPORTED_HTML5_ERROR,
  UPLOAD_VALID_CSV_ERROR,
} from "./urlFilters.constants";
import {
  ADD_URL_TO_LIST_SUCCESS,
  CANCEL_CHECK_URL,
  CHECK_CSV_FILE_FAILURE,
  CHECK_CSV_FILE_SUCCESS,
  CHECK_URL_CHANGE,
  CHECK_URL_FAILURE,
  CHECK_URL_SUCCESS,
  CLEAR_CHECK_URL,
  CLEAR_CSV_FILE,
  LOADING_FAILURE,
  LOADING_SUCCESS,
  PARSE_CSV_FILE,
  PARSE_CSV_FILE_FAILURE,
  PARSE_CSV_FILE_SUCCESS,
  REMOVE_ITEMS_FAILURE,
  REMOVE_ITEMS_SUCCESS,
  UPLOAD_CSV_FILE_FAILURE,
  UPLOAD_CSV_FILE_PARTIAL_SUCCESS,
} from "./urlFilters.types";
import { stripTrailingSlash } from "../../helpers/strings.helper";
import {
  getProfiles,
  getSubscriberInfo,
  getUrlFilters,
} from "../../store/root.selectors";

const HOST_PATH = "host-path";

export const load =
  (lists, profileId) =>
  async (dispatch, getState, { api }) => {
    const currentState = getState();
    const subscriberInfo = getSubscriberInfo(currentState);
    const profiles = getProfiles(currentState);
    const urlFilters = getUrlFilters(currentState);
    const profileName = getProfileName(profiles, profileId);

    // Is it possible that one list is empty and the second one - is not?
    const isEmptyLists = _.every(_.pick(urlFilters[profileId], lists), (list) =>
      _.isEmpty(list)
    );

    if (!isEmptyLists) return;

    const result = await api.ssm.list.get(subscriberInfo.id, profileName);

    if (isFailure(result)) {
      dispatch(getLoadFailure(result.error));
      dispatch(Notificator.error("url_filters_error"));

      return;
    }

    const data = { [profileId]: result.data };
    dispatch(loadingSuccess(data));
  };

export const removeItem =
  (list, items, profileId) =>
  async (dispatch, getState, { api }) => {
    const currentState = getState();
    const subscriberInfo = getSubscriberInfo(currentState);
    const profiles = getProfiles(currentState);
    const urlFilters = getUrlFilters(currentState);
    const profileName = getProfileName(profiles, profileId);
    const isOneUrl = items.length === 1;

    if (_.isEmpty(urlFilters[profileId][list].content)) return;

    const result = await api.ssm.list.remove(
      subscriberInfo.id,
      list,
      items,
      profileName
    );

    if (isFailure(result)) {
      dispatch(
        Notificator.error(
          isOneUrl
            ? "url_filters_removal_single_error"
            : "url_filters_removal_all_error"
        )
      );
      dispatch(getRemoveFailure(result.error));

      return;
    }

    dispatch(removeItemsSuccess(list, items, profileId));
  };

export const checkUrl =
  (url) =>
  async (dispatch, getState, { api }) => {
    if (_.isEmpty(url)) return;

    const result = await api.ssm.list.check(url);

    if (isFailure(result)) return dispatch(checkUrlFailure(result.error));

    dispatch(checkUrlSuccess(result.data));
  };

const addListFailure = () => (dispatch) => {
  dispatch(Notificator.error("url_filters_add_url_error"));
};

const checkAddCsvToListFailures =
  (result, listNodes, list, profileId) => (dispatch) => {
    const filteredHostPathUrls = filterUrlsWithPathname(listNodes);
    const filteredPublicSuffixUrls = filterUrlsWithPublicSuffix(listNodes);
    const filteredUrl = filteredUrls(listNodes);

    const hasHostPathUrls = filteredHostPathUrls.length < listNodes.length;
    const hasPublicSuffixUrls =
      filteredPublicSuffixUrls.length < listNodes.length;
    const hasUrlsFiltered = filteredUrl.length < listNodes.length;
    const hasHostPathAndPublicSuffixUrls =
      hasHostPathUrls && hasPublicSuffixUrls;

    const domainsWithPathError = hasHostPathUrls && {
      message: "host_paths_not_allowed_list",
      messageValues: {
        unprocessedPathDomains: listNodes.length - filteredHostPathUrls.length,
      },
    };
    const tldError = hasPublicSuffixUrls && {
      message: "tld_in_csv_list",
      messageValues: {
        unprocessedTldDomains:
          listNodes.length - filteredPublicSuffixUrls.length,
      },
    };
    const errorList = [domainsWithPathError, tldError].filter(Boolean);

    const failedItems =
      !_.isEmpty(result.data.failures) &&
      _.map(result.data.failures.adds, "node");
    const addedNodeList = _.map(result.data.adds, "node");

    const failureReasons = [
      {
        reason: hasHostPathAndPublicSuffixUrls,
        action: () => {
          dispatch(
            uploadCsvPartialSuccess("domains_not_allowed", errorList, {
              generalUnprocessedDomains: listNodes.length - filteredUrl.length,
              generalAllDomains: listNodes.length,
            })
          );
        },
      },
      {
        reason: hasHostPathUrls && !hasPublicSuffixUrls,
        action: () => {
          dispatch(
            uploadCsvPartialSuccess("domains_with_paths_not_allowed", [], {
              unprocessedDomains:
                listNodes.length - filteredHostPathUrls.length,
              allDomains: listNodes.length,
            })
          );
        },
      },
      {
        reason: hasPublicSuffixUrls && !hasHostPathUrls,
        action: () => {
          dispatch(uploadCsvPartialSuccess("top_level_domain_in_csv"));
        },
      },
    ];

    if (hasUrlsFiltered) {
      for (const { reason, action } of failureReasons) {
        if (reason) {
          action();
        }
      }
      return dispatch(addUrlToListSuccess(list, addedNodeList, profileId));
    }

    // That Case is valid in case if list of urls was added to BE after the check process
    // and before the sending the URLs list to BE by addUrlsToList action
    if (!_.isEmpty(failedItems)) {
      dispatch(uploadCsvFailure("partial_save_error", failedItems));
      return dispatch(addUrlToListSuccess(list, addedNodeList, profileId));
    }

    dispatch(addUrlToListSuccess(list, addedNodeList, profileId));
    dispatch(clearCheckUrlsState());
  };

const clearCheckUrlsState =
  (isManualUpload = false) =>
  (dispatch) => {
    return isManualUpload
      ? dispatch(clearCheckUrl())
      : dispatch(clearCsvFile());
  };

const urlListValidation = (
  urlFilters,
  list,
  urlNodes,
  profileId,
  isManualUpload
) => {
  const urlsList = _.map(urlNodes, ({ node }) => node);
  const { blacklist, whitelist } = urlFilters[profileId];
  const limit = urlFilters[profileId][list].limit;
  const content = urlFilters[profileId][list].content;
  const isAlreadyInCurrentList = !_.isEmpty(_.intersection(content, urlsList));
  const isAlreadyInOtherList =
    (list === ListType.Allow &&
      !_.isEmpty(_.intersection(blacklist.content, urlsList))) ||
    (list === ListType.Block &&
      !_.isEmpty(_.intersection(whitelist.content, urlsList)));
  const isLimitReached = limit < content.length + urlsList.length;

  if (isLimitReached) {
    return {
      errorNotificationType: NotificationTypes.Error,
      errorMessageId: "can_not_add_sites_limit",
      errorMessageValue: { limit },
    };
  }
  if (isAlreadyInCurrentList) {
    return {
      errorNotificationType: NotificationTypes.Warning,
      errorMessageId: isManualUpload
        ? "already_in_list"
        : "one_or_more_already_in_list",
    };
  }

  if (isAlreadyInOtherList) {
    return {
      errorNotificationType: NotificationTypes.Error,
      errorMessageId: isManualUpload
        ? "already_in_another_list"
        : "one_or_more_already_in_another_list",
    };
  }

  return {
    errorNotificationType: null,
    errorMessageId: "",
    errorMessageValue: null,
  };
};

export const addUrlsToList =
  (list, listNodes, profileId, isManualUpload = false) =>
  async (dispatch, getState, { api }) => {
    const state = getState();
    const subscriberInfo = getSubscriberInfo(state);
    const profiles = getProfiles(state);
    const profileName = getProfileName(profiles, profileId);
    const urlFilters = getUrlFilters(state);

    const { errorNotificationType, errorMessageId, errorMessageValue } =
      urlListValidation(urlFilters, list, listNodes, profileId, isManualUpload);

    if (!_.isEmpty(errorMessageId)) {
      return dispatch(
        Notificator[errorNotificationType](errorMessageId, {
          values: errorMessageValue,
        })
      );
    }

    const urlNodes = isManualUpload ? listNodes : filteredUrls(listNodes);

    const result = await api.ssm.list.add(
      subscriberInfo.id,
      list,
      urlNodes,
      profileName
    );

    if (isFailure(result)) return dispatch(addListFailure(result.error));
    if (!isManualUpload) {
      return dispatch(
        checkAddCsvToListFailures(result, listNodes, list, profileId)
      );
    }

    const nodeList = _.map(result.data.adds, "node");

    dispatch(addUrlToListSuccess(list, nodeList, profileId));
    dispatch(clearCheckUrlsState(isManualUpload));
  };

//Remove HOST_PATH nodes from list nodes
const filterUrlsWithPathname = (sites) =>
  _.filter(sites, ({ type }) => type !== HOST_PATH);

////Remove PUBLIC_SUFFIX nodes from list nodes
const filterUrlsWithPublicSuffix = (sites) =>
  _.filter(sites, ({ type }) => type !== UrlType.PUBLIC_SUFFIX);

////Remove PUBLIC_SUFFIX and host-path nodes from list nodes
const filteredUrls = (sites) =>
  _.filter(
    sites,
    ({ type }) => type !== UrlType.PUBLIC_SUFFIX && type !== HOST_PATH
  );

export const fileParser = (file, inputVal) => (dispatch) => {
  dispatch({
    type: PARSE_CSV_FILE,
  });

  const regex = /^.*\.csv$/;
  const fileName = file && file.name;
  const isValidCSVExt = file && regex.test(inputVal.toLowerCase());

  const csvParsingFailure = (errorMessage) => {
    dispatch(parsingCsvFailure(fileName, errorMessage));
  };

  if (!isValidCSVExt) {
    return csvParsingFailure(UPLOAD_VALID_CSV_ERROR);
  }

  if (typeof FileReader === "undefined") {
    return csvParsingFailure(NOT_SUPPORTED_HTML5_ERROR);
  }

  const reader = new FileReader();
  reader.onload = (e) => {
    const parsedList = e.target.result.split(/\s/g).filter(Boolean);

    if (_.isEmpty(parsedList)) {
      csvParsingFailure(EMPTY_CSV_ERROR);
    } else {
      dispatch(parsingCsvSuccess());
      dispatch(checkUrlsList(fileName, parsedList));
    }
  };
  reader.readAsText(file);
};

export const checkUrlsList =
  (fileName, siteList) =>
  async (dispatch, getState, { api }) => {
    const result = await api.ssm.list.checkUrlList(siteList);

    const csvCheckFailure = (errorMessage, errorList) => {
      dispatch(checkCsvFailure(fileName, errorMessage, errorList));
    };

    if (isFailure(result)) {
      csvCheckFailure("check_csv_error");
      dispatch(clearCheckUrl());

      return;
    }

    if (_.isEmpty(result.data.failures) && !_.isEmpty(result.data.result)) {
      const resultList = _.map(result.data.result, ({ nodes, url }) => {
        if (nodes.length > 1) {
          const urlWithoutProtocols = url.replace(/^\/\/|^.*?:\/\//, "");
          const foundNode = _.find(nodes, [
            "node",
            stripTrailingSlash(urlWithoutProtocols),
          ]);

          return (
            foundNode || {
              node: url.toLowerCase(),
              type: URL_TYPES.CORE_DOMAIN,
            }
          );
        }

        return {
          node: nodes[0].node,
          type: URL_TYPES.PUBLIC_SUFFIX,
        };
      });

      dispatch(checkCsvSuccess(fileName, resultList));
    } else {
      const failedItems = _.map(result.data.failures, "url");

      csvCheckFailure("list_url_failures", failedItems);
    }
  };

export const loadingSuccess = (data) => ({
  type: LOADING_SUCCESS,
  payload: data,
});
export const removeItemsSuccess = (list, items, profileId) => ({
  type: REMOVE_ITEMS_SUCCESS,
  payload: { list, items, profileId },
});
export const checkUrlChange = (url) => ({
  type: CHECK_URL_CHANGE,
  payload: { url },
});
export const checkUrlSuccess = (urls) => ({
  type: CHECK_URL_SUCCESS,
  payload: { urls },
});
export const checkUrlFailure = (error) => ({
  type: CHECK_URL_FAILURE,
  payload: error,
});
export const clearCheckUrl = () => ({
  type: CLEAR_CHECK_URL,
});
export const cancelCheckUrl = () => ({
  type: CANCEL_CHECK_URL,
});
export const addUrlToListSuccess = (list, item, profileId) => ({
  type: ADD_URL_TO_LIST_SUCCESS,
  payload: { list, item, profileId },
});
export const parsingCsvSuccess = () => ({
  type: PARSE_CSV_FILE_SUCCESS,
});
export const checkCsvSuccess = (fileName, siteList) => ({
  type: CHECK_CSV_FILE_SUCCESS,
  payload: { fileName, siteList },
});
export const parsingCsvFailure = (fileName, errorMessage) => ({
  type: PARSE_CSV_FILE_FAILURE,
  payload: { fileName, errorMessage },
});
// Payload: { fileName, errorMessage, errorList? }
export const checkCsvFailure = (fileName, errorMessage, errorList) => ({
  type: CHECK_CSV_FILE_FAILURE,
  payload: { fileName, errorMessage, errorList },
});
// Payload: { errorMessage, errorList?, errorMessageValues? }
export const uploadCsvFailure = (
  errorMessage,
  errorList,
  errorMessageValues
) => ({
  type: UPLOAD_CSV_FILE_FAILURE,
  payload: { errorMessage, errorList, errorMessageValues },
});
//Payload: { warningMessage, warningList?, warningMessageValues? }
export const uploadCsvPartialSuccess = (
  warningMessage,
  warningList,
  warningMessageValues
) => ({
  type: UPLOAD_CSV_FILE_PARTIAL_SUCCESS,
  payload: { warningMessage, warningList, warningMessageValues },
});
export const clearCsvFile = () => ({
  type: CLEAR_CSV_FILE,
});
export const getLoadFailure = (error) => ({
  type: LOADING_FAILURE,
  payload: error,
});
export const getRemoveFailure = (error) => ({
  type: REMOVE_ITEMS_FAILURE,
  payload: error,
});

const getProfileName = (profiles, profileId) => {
  const profilesList = profiles && profiles.saved.list;

  return profileId === GLOBAL_URL_FILTER_ID ? "" : profilesList[profileId].name;
};
